<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ isEdit ? $t('microLearning.moduleForm.editTitle') : $t('microLearning.moduleForm.createTitle') }}</h4>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<button type="button" class="btn btn-outline-secondary" @click="$router.push('/micro-learning/modules')">{{ $t('common.actions.back') }}</button>
				</div>
			</div>

			<!-- Module info -->
			<div class="col-lg-12 mb-6">
				<div class="card">
					<div class="card-body">
						<div class="row g-5">
							<div class="col-12">
								<div class="form-floating form-floating-outline">
									<input
										type="text"
										class="form-control"
										:class="{ 'is-invalid': errors.name }"
										:placeholder="$t('microLearning.moduleForm.namePlaceholder')"
										v-model="module.name"
										maxlength="200"
										autocomplete="off"
									/>
									<label>{{ $t('microLearning.moduleForm.nameLabel') }} *</label>
									<div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
								</div>
							</div>
							<div class="col-12">
								<div class="form-floating form-floating-outline">
									<textarea
										class="form-control"
										:class="{ 'is-invalid': errors.description }"
										:placeholder="$t('microLearning.moduleForm.descriptionPlaceholder')"
										v-model="module.description"
										maxlength="500"
										style="height: 80px;"
									></textarea>
									<label>{{ $t('microLearning.moduleForm.descriptionLabel') }}</label>
									<div class="invalid-feedback" v-if="errors.description">{{ errors.description }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Questions -->
			<div class="col-lg-12 mb-6">
				<div class="card">
					<div class="card-header d-flex justify-content-between align-items-center">
						<h5 class="mb-0">{{ $t('microLearning.moduleForm.questionsSection') }} ({{ module.questions.length }}/5)</h5>
						<button type="button" class="btn btn-sm btn-primary" @click="addQuestion" :disabled="module.questions.length >= 5">
							<i class="ri-add-line me-1"></i>{{ $t('microLearning.moduleForm.addQuestion') }}
						</button>
					</div>
					<div class="card-body">
						<div class="text-danger small mb-4" v-if="errors.questions">{{ errors.questions }}</div>

						<draggable
							v-model="module.questions"
							item-key="key"
							handle=".drag-handle"
							animation="200"
						>
							<template #item="{ element: question, index: qIndex }">
								<div class="accordion mb-3" :id="'accordionQ' + qIndex">
									<div class="accordion-item border shadow-none">
										<h2 class="accordion-header d-flex align-items-center">
											<i class="ri-draggable drag-handle me-2 ms-3" style="cursor: grab; font-size: 1.2rem;"></i>
											<button
												class="accordion-button flex-grow-1"
												:class="{ collapsed: !question.open }"
												type="button"
												@click="question.open = !question.open"
											>
												<span class="d-flex align-items-center gap-2">
													<span class="badge bg-label-primary rounded-pill">{{ qIndex + 1 }}</span>
													<span v-if="question.question">{{ question.question }}</span>
													<span v-else class="text-muted fst-italic">{{ $t('microLearning.moduleForm.questionTextPlaceholder') }}</span>
													<span class="badge bg-label-success ms-2" v-if="question.validAnswerIndices.length > 0">
														{{ question.validAnswerIndices.length }} {{ $t('microLearning.moduleForm.correctAnswer').toLowerCase() }}
													</span>
												</span>
											</button>
											<button type="button" class="btn btn-sm btn-icon btn-text-danger me-3" @click.stop="removeQuestion(qIndex)" :disabled="module.questions.length <= 3" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.moduleForm.removeQuestion')">
												<i class="ri-delete-bin-6-line"></i>
											</button>
										</h2>
										<div class="accordion-collapse" :class="{ collapse: !question.open, show: question.open }">
											<div class="accordion-body pt-5">
												<!-- Question text -->
												<div class="mb-4">
													<div class="form-floating form-floating-outline">
														<input
															type="text"
															class="form-control"
															:class="{ 'is-invalid': errors['question_' + qIndex + '_text'] }"
															:placeholder="$t('microLearning.moduleForm.namePlaceholder')"
															v-model="question.question"
															autocomplete="off"
														/>
														<label>{{ $t('microLearning.moduleForm.questionTextPlaceholder') }}</label>
														<div class="invalid-feedback" v-if="errors['question_' + qIndex + '_text']">{{ errors['question_' + qIndex + '_text'] }}</div>
													</div>
												</div>

												<!-- Answers -->
												<div class="d-flex justify-content-between align-items-center mb-3">
													<label class="fw-medium">{{ $t('microLearning.moduleForm.answers') }} ({{ question.answers.length }}/6)</label>
													<button type="button" class="btn btn-sm btn-outline-primary" @click="addAnswer(qIndex)" :disabled="question.answers.length >= 6">
														<i class="ri-add-line me-1"></i>{{ $t('microLearning.moduleForm.addAnswer') }}
													</button>
												</div>
												<div class="text-danger small mb-3" v-if="errors['question_' + qIndex + '_answers']">{{ errors['question_' + qIndex + '_answers'] }}</div>

												<div v-for="(answer, aIndex) in question.answers" :key="aIndex" class="d-flex align-items-center gap-3 mb-3">
													<div class="form-check">
														<input
															class="form-check-input"
															type="checkbox"
															:id="'q' + question.key + '_a' + aIndex"
															:checked="question.validAnswerIndices.includes(aIndex)"
															@change="toggleValidAnswer(qIndex, aIndex)"
														/>
													</div>
													<div class="form-floating form-floating-outline flex-grow-1">
														<input
															type="text"
															class="form-control"
															:placeholder="$t('microLearning.moduleForm.answerPlaceholder')"
															v-model="question.answers[aIndex]"
															autocomplete="off"
														/>
														<label>{{ $t('microLearning.moduleForm.answerPlaceholder') }}</label>
													</div>
													<button type="button" class="btn btn-sm btn-icon btn-outline-danger" @click="removeAnswer(qIndex, aIndex)" :disabled="question.answers.length <= 2">
														<i class="ri-close-line"></i>
													</button>
												</div>
												<small class="text-muted"><i class="ri-checkbox-circle-line me-1"></i>{{ $t('microLearning.moduleForm.correctAnswer') }}</small>
											</div>
										</div>
									</div>
								</div>
							</template>
						</draggable>
					</div>
				</div>
			</div>

			<!-- Explanations -->
			<div class="col-lg-12" v-if="module.questions.length > 0">
				<div class="card">
					<div class="card-header">
						<h5 class="mb-0"><i class="ri-chat-quote-line me-2"></i>{{ $t('microLearning.moduleForm.explanations') || 'Explanations' }}</h5>
						<small class="text-muted">{{ $t('microLearning.moduleForm.explanationsHint') || 'Optional: add an explanation shown after each answer' }}</small>
					</div>
					<div class="card-body">
						<div v-for="(question, qIndex) in module.questions" :key="'exp-' + qIndex" class="mb-4">
							<label class="form-label fw-medium">Q{{ qIndex + 1 }}: {{ question.question || '...' }}</label>
							<div class="form-floating form-floating-outline">
								<textarea
									class="form-control"
									:placeholder="$t('microLearning.moduleForm.explanationPlaceholder') || 'Explanation shown after the employee answers...'"
									v-model="question.explanation"
									rows="2"
									style="height: auto; min-height: 60px;"
								></textarea>
								<label>{{ $t('microLearning.moduleForm.explanation') || 'Explanation' }}</label>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Submit -->
			<div class="col-lg-12 d-flex justify-content-end gap-4 mt-4 mb-6">
				<button type="button" class="btn btn-outline-secondary" @click="$router.push('/micro-learning/modules')">{{ $t('common.actions.cancel') }}</button>
				<button type="button" class="btn btn-primary" @click="submitModule" :disabled="isSubmitting">
					{{ $t('common.actions.save') }}
				</button>
			</div>

		</div>
		<!-- SUCCESS MODAL -->
		<div class="modal fade" :class="{ show: showSuccess }" :style="showSuccess ? 'display: block;' : ''" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple modal-add-new-cc">
				<div class="modal-content">
					<div class="modal-body p-0">
						<div class="text-center">
							<CheckIcon />
							<p class="text-muted mt-5 small">
								{{ isEdit ? $t('microLearning.moduleForm.editSuccess') : $t('microLearning.moduleForm.createSuccess') }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade" :class="{ show: showSuccess }" v-if="showSuccess"></div>

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import draggable from "vuedraggable";
import CheckIcon from "@/components/CheckIcon";

export default {
	name: "MicroLearningModuleForm",

	components: { draggable, CheckIcon },

	data() {
		return {
			isEdit: false,
			isSubmitting: false,
			showSuccess: false,
			module: {
				name: "",
				description: "",
				questions: [],
			},
			errors: {},
			keyCounter: 0,
		};
	},

	methods: {
		generateKey() {
			return ++this.keyCounter;
		},

		createEmptyQuestion(open = false) {
			return {
				key: this.generateKey(),
				question: "",
				questionImage: null,
				answers: ["", ""],
				validAnswerIndices: [],
				explanation: "",
				open: open,
			};
		},

		addQuestion() {
			if (this.module.questions.length >= 5) return;
			this.module.questions.push(this.createEmptyQuestion(true));
		},

		removeQuestion(index) {
			if (this.module.questions.length <= 3) return;
			this.module.questions.splice(index, 1);
		},

		addAnswer(qIndex) {
			if (this.module.questions[qIndex].answers.length >= 6) return;
			this.module.questions[qIndex].answers.push("");
		},

		removeAnswer(qIndex, aIndex) {
			const question = this.module.questions[qIndex];
			if (question.answers.length <= 2) return;
			question.answers.splice(aIndex, 1);
			// Update validAnswerIndices: remove the deleted index and shift higher ones
			question.validAnswerIndices = question.validAnswerIndices
				.filter(i => i !== aIndex)
				.map(i => i > aIndex ? i - 1 : i);
		},

		toggleValidAnswer(qIndex, aIndex) {
			const question = this.module.questions[qIndex];
			const idx = question.validAnswerIndices.indexOf(aIndex);
			if (idx === -1) {
				question.validAnswerIndices.push(aIndex);
			} else {
				question.validAnswerIndices.splice(idx, 1);
			}
		},

		validateForm() {
			this.errors = {};

			if (!this.module.name || !this.module.name.trim()) {
				this.errors.name = this.$t("microLearning.moduleForm.nameRequired");
			} else if (this.module.name.length > 200) {
				this.errors.name = this.$t("microLearning.moduleForm.nameMaxLength");
			}

			if (this.module.description && this.module.description.length > 500) {
				this.errors.description = this.$t("microLearning.moduleForm.descriptionMaxLength");
			}

			if (this.module.questions.length < 3 || this.module.questions.length > 5) {
				this.errors.questions = this.$t("microLearning.moduleForm.minQuestions");
			}

			this.module.questions.forEach((q, i) => {
				if (!q.question || !q.question.trim()) {
					this.errors[`question_${i}_text`] = this.$t("microLearning.moduleForm.questionTextRequired", { n: i + 1 });
				}
				if (q.answers.length < 2 || q.answers.length > 6) {
					this.errors[`question_${i}_answers`] = this.$t("microLearning.moduleForm.minAnswers", { n: i + 1 });
				}
				const hasEmptyAnswer = q.answers.some((a) => !a || !a.trim());
				if (hasEmptyAnswer) {
					this.errors[`question_${i}_answers`] = this.$t("microLearning.moduleForm.answerTextRequired", { n: i + 1 });
				}
				if (q.validAnswerIndices.length === 0) {
					this.errors[`question_${i}_answers`] = this.$t("microLearning.moduleForm.atLeastOneCorrect", { n: i + 1 });
				}
			});

			// Open questions that have errors
			if (Object.keys(this.errors).length > 0) {
				this.module.questions.forEach((q, i) => {
					if (this.errors[`question_${i}_text`] || this.errors[`question_${i}_answers`]) {
						q.open = true;
					}
				});
			}

			return Object.keys(this.errors).length === 0;
		},

		buildPayload() {
			return {
				name: this.module.name.trim(),
				description: this.module.description ? this.module.description.trim() : null,
				questions: this.module.questions.map((q) => ({
					question: q.question.trim(),
					questionImage: q.questionImage || null,
					answers: q.answers.map((a) => a.trim()),
					validAnswers: q.validAnswerIndices.map((i) => q.answers[i].trim()),
					explanation: q.explanation ? q.explanation.trim() : null,
				})),
			};
		},

		async submitModule() {
			if (!this.validateForm()) return;
			this.isSubmitting = true;

			const payload = this.buildPayload();

			try {
				if (this.isEdit) {
					await HTTP.put(`/micro-learning/modules/${this.$route.params.id}`, payload);
				} else {
					await HTTP.post("/micro-learning/modules", payload);
				}
				this.showSuccess = true;
				setTimeout(() => {
					this.$router.push("/micro-learning/modules");
				}, 1500);
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isSubmitting = false;
			}
		},

		async fetchModule() {
			try {
				const response = await HTTP.get(`/micro-learning/modules/${this.$route.params.id}`);
				const mod = response.data.module || response.data;

				this.module.name = mod.name;
				this.module.description = mod.description || "";
				this.module.questions = mod.questions.map((q) => {
					const validIndices = q.validAnswers
						.map((va) => q.answers.indexOf(va))
						.filter((i) => i !== -1);
					return {
						key: this.generateKey(),
						question: q.question,
						questionImage: q.questionImage || null,
						answers: [...q.answers],
						validAnswerIndices: validIndices,
						explanation: q.explanation || "",
						open: false,
					};
				});
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
				this.$router.push("/micro-learning/modules");
			}
		},

		initQuestions() {
			this.module.questions = [
				this.createEmptyQuestion(true),
				this.createEmptyQuestion(false),
				this.createEmptyQuestion(false),
			];
		},
	},

	async mounted() {
		if (this.$route.params.id) {
			this.isEdit = true;
			await this.fetchModule();
		} else {
			this.initQuestions();
		}
	},
};
</script>

<style scoped>
.drag-handle:hover {
	color: var(--bs-primary);
}

.accordion-button:not(.collapsed) {
	background-color: rgba(var(--bs-primary-rgb), 0.05);
}

.accordion-button::after {
	margin-left: auto;
}

.accordion-button:focus {
	box-shadow: none;
}
</style>
